/* HotStep PDP — vanilla JS, no dependencies. Loaded with `defer`; safe to include from several sections. */
(() => {
  if (window.__hsPdp) return;
  window.__hsPdp = true;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = reduceMotion ? 'auto' : 'smooth';

  /* ---------- Money ---------- */
  const fmtNum = (cents, dec, thou, sep) => {
    const [i, d] = (cents / 100).toFixed(dec).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (d ? sep + d : '');
  };
  const money = (cents, format) => {
    format = format || '${{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: fmtNum(cents, 2, ',', '.'),
      amount_no_decimals: fmtNum(cents, 0, ',', '.'),
      amount_with_comma_separator: fmtNum(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmtNum(cents, 0, '.', ','),
      amount_with_apostrophe_separator: fmtNum(cents, 2, "'", '.')
    }[k] ?? fmtNum(cents, 2, ',', '.')));
  };

  /* ---------- Sticky header offset (no polling) ----------
     Uses the header's bottom edge, so a header that slides in or out is followed exactly.
     Some themes (Impulse: .site-header--stuck) switch the header to position:fixed only after
     scrolling, via a class change — a ResizeObserver never sees that, so the class and the
     slide animation are watched too. */
  const header = $('.section-header, sticky-header, header.header, header');
  // The pinned element may be the header itself or a wrapper around it (a header section that
  // adds a fixed announcement strip pins its wrapper), so the header and its ancestors are checked.
  const pinned = () => {
    for (let el = header; el && el !== document.body; el = el.parentElement) {
      const pos = getComputedStyle(el).position;
      if (pos === 'sticky' || pos === 'fixed') return el;
    }
    return null;
  };
  let lastHdr = -1;
  const setHdr = () => {
    if (!header) return;
    const pin = pinned();
    const h = pin ? Math.max(0, Math.round(Math.max(pin.getBoundingClientRect().bottom, header.getBoundingClientRect().bottom))) : 0;
    if (h === lastHdr) return;
    lastHdr = h;
    document.documentElement.style.setProperty('--hs-hdr', h + 'px');
    setPush(pin);
  };
  // A header that is fixed over the page (MSG: announcement strip + header in one fixed wrapper) takes
  // no room, so the product section would start underneath it. Push the section down by the part of
  // the header that overlaps the top of the page.
  let lastPush = -1;
  const setPush = pin => {
    const main = document.querySelector('.hs-main');
    if (!main) return;
    let push = 0;
    if (pin && getComputedStyle(pin).position === 'fixed') {
      // Where the header sits when it is fully shown: its resting top plus its height (sliding it away on scroll doesn't change this)
      const hdrBottom = (parseFloat(getComputedStyle(pin).top) || 0) + Math.max(pin.offsetHeight, header.offsetHeight);
      const pageTop = main.closest('.shopify-section')?.offsetTop ?? 0;
      push = Math.max(0, Math.round(hdrBottom - pageTop));
    }
    if (push === lastPush) return;
    lastPush = push;
    main.style.setProperty('--hs-push', push + 'px');
  };
  if (header) {
    if ('ResizeObserver' in window) new ResizeObserver(setHdr).observe(header);
    for (let el = header; el && el !== document.body; el = el.parentElement) {
      new MutationObserver(setHdr).observe(el, { attributes: true, attributeFilter: ['class', 'style'] });
    }
    header.addEventListener('transitionend', setHdr);
    header.addEventListener('animationend', setHdr);
    // Wrappers can be pinned by the theme's scroll script without touching the header: re-check once per frame while scrolling
    let raf = 0;
    addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; setHdr(); }); }, { passive: true });
  }
  setHdr();

  /* ---------- Cart ---------- */
  const routes = window.Shopify?.routes?.root || '/';
  /* "Estimated to Ship by" line property — the same value the cart page (sections/main-cart.liquid)
     writes, from the variant metafields custom.variant_instock_date and
     custom.estimated_ship_time. Sending it at add time means it reaches the cart and the
     order even if the shopper skips the cart page, and the cart page finds nothing to rewrite. */
  function cartShipEstimate(v) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (v && v.instock) {
      const inStockVDate = new Date(v.instock); inStockVDate.setHours(0, 0, 0, 0);
      if (inStockVDate < today) return 'In Stock';
      const startDate = new Date(v.instock), startDateObj = new Date(v.instock);
      const diffDaysOne = Math.ceil((startDate - today) / 864e5);
      const diffDays = Math.ceil((new Date(startDate) - today) / 864e5);
      let minus;
      if (diffDaysOne > 14) minus = 14;
      else if (diffDaysOne > 2) minus = diffDays - 3;
      else minus = diffDays - 1;
      startDate.setDate(startDateObj.getDate() - minus);
      const fmt = d => d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
      return `${fmt(startDate)} - ${fmt(inStockVDate)}`;
    }
    const range = String((v && v.ship) || '').split('-');
    const dt1 = Number(range[0]), dt2 = Number(range[1]);
    if (dt1 > 0 && dt2 > 0) {
      const now = new Date(), a = new Date(), b = new Date();
      a.setDate(now.getDate() + dt1); b.setDate(now.getDate() + dt2);
      const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${fmt(a)} - ${fmt(b)}`;
    }
    return 'In Stock';
  }

  async function cartAdd(items, btn) {
    if (btn) btn.setAttribute('aria-busy', 'true');
    const drawer = $('cart-drawer') || $('cart-notification');
    const body = { items };
    if (drawer?.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map(s => s.id);
      body.sections_url = location.pathname;
    }
    try {
      const res = await fetch(routes + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      document.dispatchEvent(new CustomEvent('hs:cart-added', { detail: data }));
      // Impulse in drawer mode: the theme's own event builds and opens its cart drawer (and apps
      // listening for it react), exactly as the theme's product form does. Page mode: go to /cart.
      if (window.theme?.settings?.cartType === 'drawer' && document.getElementById('CartDrawer')) {
        const first = (data.items && data.items[0]) || data;
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: first, addToCartBtn: btn } }));
        return true;
      }
      if (drawer?.renderContents) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      } else if (document.dispatchEvent(new CustomEvent('hs:cart-open', { cancelable: true, detail: data }))) {
        // No theme listener called preventDefault() to open its own drawer → go to the cart page.
        location.href = routes + 'cart';
      }
      return true;
    } catch (err) {
      document.dispatchEvent(new CustomEvent('hs:cart-error', { detail: err.message }));
      return err.message;
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }

  // Variant names like "GFCI Power Unit (120V) / Not Applicable": drop placeholder option values
  const cleanVariantTitle = t => String(t || '').split(' / ')
    .filter(x => !/^(not applicable|n\/a|none|default title|default)$/i.test(x.trim())).join(' / ');

  /* ---------- Gallery toolbar: hide jump links whose target section isn't on the page ---------- */
  const hideDeadJumps = () => $$('.hs-gallery__tools a[href^="#"]').forEach(a => {
    if (a.dataset.hsFallback) return;                     // e.g. Comments falls back to reviews
    const id = decodeURIComponent(a.getAttribute('href').slice(1));
    if (id && !document.getElementById(id)) a.hidden = true;
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hideDeadJumps); else hideDeadJumps();

  /* ---------- Complete your setup: ticking an item updates the count, total and button ---------- */
  document.addEventListener('change', e => {
    if (!e.target.matches('[data-setup-item]')) return;
    const box = e.target.closest('.hs-setup'), all = $$('[data-setup-item]', box), on = all.filter(i => i.checked);
    const total = on.reduce((s, i) => s + +i.dataset.price, 0);
    $('[data-setup-sum]', box).innerHTML = on.length ? `${on.length} ${on.length === 1 ? 'item' : 'items'} · <b>${money(total, box.dataset.money)}</b>` : 'Tick the items you need';
    const btn = $('.hs-setup__add', box);
    btn.disabled = !on.length;
    btn.textContent = on.length > 1 && on.length === all.length ? 'Add all to cart' : on.length > 1 ? `Add ${on.length} to cart` : 'Add to cart';
  });

  /* ---------- Global delegated handlers ---------- */
  document.addEventListener('click', async e => {
    const add = e.target.closest('[data-hs-add]');
    if (add) {
      e.preventDefault();
      let items = JSON.parse(add.dataset.hsAdd);
      // Same "Estimated to Ship by" note the cart page writes, so these lines reach the cart
      // (and the order) complete and the cart page has nothing to rewrite.
      let meta = []; try { meta = JSON.parse(add.dataset.hsShipMeta || '[]'); } catch (_) {}
      items.forEach((it, i) => { if (meta[i]) it.properties = { 'Estimated to Ship by': cartShipEstimate(meta[i]) }; });
      // Complete your setup: only the ticked items
      const setup = add.closest('.hs-setup');
      if (setup) {
        const on = new Set($$('[data-setup-item]:checked', setup).map(i => +i.dataset.setupItem));
        items = items.filter((_, i) => on.has(i));
        if (!items.length) return;
      }
      const r = await cartAdd(items, add);
      if (r !== true) alertNear(add, r);
      return;
    }
    const copy = e.target.closest('[data-hs-copy]');
    if (copy) {
      e.preventDefault();
      const code = copy.dataset.hsCopy;
      let ok = false;
      try { await navigator.clipboard.writeText(code); ok = true; } catch (_) {
        // Older Safari / non-secure contexts: fall back to a hidden textarea
        const t = Object.assign(document.createElement('textarea'), { value: code });
        t.setAttribute('readonly', ''); t.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
        document.body.appendChild(t); t.select(); t.setSelectionRange(0, code.length); // iOS needs the range
        try { ok = document.execCommand('copy'); } catch (_) {}
        t.remove();
      }
      const label = $('[data-hs-copy-label]', copy);
      if (label && ok) {
        clearTimeout(copy._t);
        copy._html = copy._html || label.innerHTML;
        label.textContent = copy.dataset.hsCopyDone || `Copied: ${code}`;
        copy.classList.add('is-copied');
        copy._t = setTimeout(() => { label.innerHTML = copy._html; copy.classList.remove('is-copied'); }, 2000);
      }
      return;
    }
    const wish = e.target.closest('[data-hs-wish]');
    if (wish) {
      const on = wish.getAttribute('aria-pressed') !== 'true';
      wish.setAttribute('aria-pressed', String(on));
      try { localStorage.setItem('hs-wish-' + wish.dataset.hsWish, on ? '1' : ''); } catch (_) {}
      document.dispatchEvent(new CustomEvent('hs:wishlist', { detail: { handle: wish.dataset.hsWish, saved: on } }));
      return;
    }
    const pick = e.target.closest('[data-hs-select]');
    if (pick) {
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('hs:select', { detail: pick.dataset.hsSelect }));
      jump('#hs-buy-box');
      return;
    }
    const link = e.target.closest('a[href^="#hs-"], a[href="#reviews"], .hs-gallery__tools a[href^="#"]');
    if (link) {
      const target = resolveTarget(link);
      if (target) { e.preventDefault(); jump(target); }
    }
  });

  $$('[data-hs-wish]').forEach(b => {
    try { if (localStorage.getItem('hs-wish-' + b.dataset.hsWish)) b.setAttribute('aria-pressed', 'true'); } catch (_) {}
  });

  // Reviews apps render their own markup with no id we control; these cover the common ones.
  const REVIEW_FALLBACK = '#reviews, #shopify-product-reviews, .jdgm-rev-widget, .jdgm-widget, .spr-container, ' +
    '.yotpo-widget-instance, .yotpo-main-widget, .okeReviews, .stamped-main-widget, .loox-reviews-default, ' +
    '.ryviu-widget, .r--widget, [id*="judgeme" i], [class*="review"][class*="widget"]';
  function resolveTarget(link) {
    const href = link.getAttribute('href') || '';
    let el = href.length > 1 ? document.getElementById(href.slice(1)) : null;
    if (!el && link.dataset.hsFallback === 'reviews') el = document.getElementById('reviews') || $(REVIEW_FALLBACK);   // the HS · Anchor above the reviews wins over app markup
    return el;
  }

  function jump(target) {
    const el = typeof target === 'string' ? $(target) : target;
    if (!el) return;
    el.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
    settle(el);
  }
  // From the top of the page the store header isn't sticky yet, so the scroll offset is measured
  // without it and the target lands under the header. Once scrolling stops, nudge it into place.
  function settle(el) {
    let t, done = false;
    const stop = () => { done = true; clearTimeout(t); removeEventListener('scroll', onScroll); };
    const fix = () => {
      if (done) return; stop();
      const want = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
      const off = el.getBoundingClientRect().top - want;
      if (Math.abs(off) > 4) scrollBy({ top: off, behavior: scrollBehavior });
    };
    const onScroll = () => { clearTimeout(t); t = setTimeout(fix, 150); };
    addEventListener('scroll', onScroll, { passive: true });
    t = setTimeout(fix, 600);                 // no scroll happened (already there)
    setTimeout(stop, 4000);                   // never chase the page for long
  }
  function alertNear(el, msg) {
    let n = el.parentElement.querySelector('.hs-inline-error');
    if (!n) {
      n = document.createElement('p');
      n.className = 'hs-inline-error';
      n.setAttribute('role', 'alert');
      n.style.cssText = 'color:var(--hs-sale);font-size:14px;margin-top:8px';
      el.after(n);
    }
    n.textContent = msg;
  }

  /* ---------- Product data (shared by main product + compare) ---------- */
  let dataCache;
  const productData = () => {
    if (dataCache) return dataCache;
    const node = $('script[data-hs-product-json]');
    return (dataCache = node ? JSON.parse(node.textContent) : null);
  };
  const shortLabel = s => String(s || '').split(/\s*[:\u2013\u2014|]\s*/)[0].trim();
  const findVariant = (d, opts) => d.variants.find(v => v.options.every((o, i) => opts[i] == null || o === opts[i]));

  /* ---------- <hs-gallery> scroll-snap slider + media toolbar ---------- */
  define('hs-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-hs-track]', this);
      this.slides = $$('[data-media-id]', this.track);
      this.thumbs = $$('[data-hs-thumb]', this);
      this.rail = $('[data-hs-thumbs]', this);
      this.railNavs = $$('[data-hs-thumb-scroll]', this);
      this.box = $('[data-hs-lightbox]', this);
      this.addEventListener('click', e => {
        const t = e.target.closest('[data-hs-thumb]');
        if (t) return this.go(+t.dataset.hsThumb);
        const rs = e.target.closest('[data-hs-thumb-scroll]');
        if (rs) return this.scrollRail(+rs.dataset.hsThumbScroll);
        const n = e.target.closest('[data-hs-step]');
        if (n) return this.go(this.index + +n.dataset.hsStep);
        const tool = e.target.closest('[data-hs-tool]');
        if (tool) return tool.dataset.hsTool === 'video' ? this.playVideo() : this.zoom();
        if (e.target.closest('[data-hs-lightbox-close]') || e.target === this.box) this.box?.close();
      });
      // A link with nothing to scroll to is worse than no link.
      $$('.hs-gallery__tools a[href^="#"]', this).forEach(a => { a.hidden = !resolveTarget(a); });
      this.index = 0;
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.mark(i);
        });
      }, { passive: true });

      // Thumbnails live in one scrolling row; the arrows only show when it actually overflows.
      if (this.rail) {
        const sync = () => this.syncRail();
        this.rail.addEventListener('scroll', sync, { passive: true });
        if ('ResizeObserver' in window) new ResizeObserver(sync).observe(this.rail);
        sync();
      }
    }
    scrollRail(dir) {
      if (!this.rail) return;
      this.rail.scrollBy({ left: dir * Math.max(120, this.rail.clientWidth * 0.8), behavior: scrollBehavior });
    }
    syncRail() {
      const r = this.rail;
      if (!r) return;
      const max = r.scrollWidth - r.clientWidth;
      this.railNavs.forEach(b => {
        b.hidden = max <= 2 || (+b.dataset.hsThumbScroll < 0 ? r.scrollLeft <= 2 : r.scrollLeft >= max - 2);
      });
    }
    revealThumb(i) {
      const li = this.thumbs[i]?.parentElement;
      const r = this.rail;
      if (!li || !r) return;
      const left = li.offsetLeft;
      const right = left + li.offsetWidth;
      if (left < r.scrollLeft) r.scrollTo({ left: Math.max(0, left - 10), behavior: scrollBehavior });
      else if (right > r.scrollLeft + r.clientWidth) r.scrollTo({ left: right - r.clientWidth + 10, behavior: scrollBehavior });
    }
    go(i) {
      const n = this.slides.length;
      i = (i + n) % n;
      this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: scrollBehavior });
      this.mark(i);
    }
    goToMedia(id) {
      const i = this.slides.findIndex(s => s.dataset.mediaId == id);
      if (i > -1) this.go(i);
    }
    mark(i) {
      this.index = i;
      this.thumbs.forEach((t, k) => t.setAttribute('aria-current', k === i ? 'true' : 'false'));
      this.revealThumb(i);
      // Pause any video the shopper has scrolled away from.
      this.slides.forEach((s, k) => { if (k !== i) $('video', s)?.pause(); });
    }
    playVideo() {
      const i = this.slides.findIndex(s => s.dataset.mediaType !== 'image');
      if (i < 0) return;
      this.go(i);
      const v = $('video', this.slides[i]);
      if (v) { v.muted = true; v.play().catch(() => {}); }
    }
    zoom() {
      const src = this.slides[this.index]?.dataset.zoom;
      if (!src) return;
      if (this.box?.showModal) {
        let img = $('img', this.box);
        if (!img) {
          img = document.createElement('img');
          img.alt = this.box.dataset.alt || '';
          this.box.append(img);
        }
        img.src = src;
        this.box.showModal();
      } else {
        window.open(src, '_blank', 'noopener');
      }
    }
  });

  /* ---------- <hs-carousel> generic auto-advancing slider ----------
     One step = one column. Autoplay stops on hover, on keyboard focus, while the tab is
     hidden, while the slider is off-screen, and for anyone who prefers reduced motion. */
  define('hs-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-hs-carousel-track]', this);
      if (!this.track) return;
      this.cells = Array.from(this.track.children);
      this.arrows = $$('[data-hs-carousel-step]', this);
      this.dots = $$('[data-hs-carousel-dot]', this);
      this.toggle = $('[data-hs-carousel-toggle]', this);
      this.delay = Math.max(0, +this.dataset.autoplay || 0);
      this.index = 0;
      this.paused = false;

      this.addEventListener('click', e => {
        const step = e.target.closest('[data-hs-carousel-step]');
        if (step) { this.stop(true); return this.go(this.index + +step.dataset.hsCarouselStep); }
        const dot = e.target.closest('[data-hs-carousel-dot]');
        if (dot) { this.stop(true); return this.go(+dot.dataset.hsCarouselDot); }
        if (e.target.closest('[data-hs-carousel-toggle]')) this.stop(!this.paused);
      });

      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.mark(this.readIndex()));
      }, { passive: true });

      // Any hand on the wheel wins over the timer.
      this.addEventListener('pointerenter', () => this.hold(true));
      this.addEventListener('pointerleave', () => this.hold(false));
      this.addEventListener('focusin', () => this.hold(true));
      this.addEventListener('focusout', () => { if (!this.contains(document.activeElement)) this.hold(false); });
      this.track.addEventListener('pointerdown', () => this.stop(true), { passive: true });
      document.addEventListener('visibilitychange', () => this.tick());

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => { this.onScreen = en.isIntersecting; this.tick(); },
          { threshold: 0.2 }).observe(this);
      } else {
        this.onScreen = true;
      }
      if (reduceMotion) this.delay = 0;
      this.mark(0);
      this.tick();
    }
    step() {
      // Measured, not assumed: one column plus its gap.
      return this.cells.length > 1 ? (this.cells[1].offsetLeft - this.cells[0].offsetLeft) : this.track.clientWidth;
    }
    readIndex() {
      const s = this.step();
      return s > 0 ? Math.round(this.track.scrollLeft / s) : 0;
    }
    go(i) {
      const max = this.lastIndex();
      if (i > max) i = 0;
      else if (i < 0) i = max;
      this.track.scrollTo({ left: Math.min(i * this.step(), this.track.scrollWidth), behavior: reduceMotion ? 'auto' : 'smooth' });
      this.mark(i);
    }
    lastIndex() {
      const s = this.step();
      return s > 0 ? Math.max(0, Math.round((this.track.scrollWidth - this.track.clientWidth) / s)) : 0;
    }
    mark(i) {
      this.index = i;
      this.dots.forEach((d, k) => d.setAttribute('aria-selected', k === i ? 'true' : 'false'));
      const max = this.lastIndex();
      this.arrows.forEach(a => { a.disabled = max === 0; });
      this.dots.forEach((d, k) => { d.hidden = k > max; });
    }
    hold(on) { this.held = on; this.tick(); }
    stop(on) {
      this.paused = on;
      if (this.toggle) {
        this.toggle.setAttribute('aria-pressed', String(on));
        this.toggle.setAttribute('aria-label', on ? 'Resume automatic sliding' : 'Pause automatic sliding');
        const pause = $('.hs-uc__play-pause', this.toggle);
        const play = $('.hs-uc__play-go', this.toggle);
        if (pause) pause.hidden = on;
        if (play) play.hidden = !on;
      }
      this.tick();
    }
    tick() {
      clearInterval(this.timer);
      const run = this.delay > 0 && !this.paused && !this.held && this.onScreen !== false && !document.hidden;
      if (run) this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  });

  /* ---------- <hs-eta> delivery estimate ---------- */
  define('hs-eta', class extends HTMLElement {
    connectedCallback() {
      const days = +this.dataset.days || 3;
      const cutoff = +this.dataset.cutoff || 14;
      const d = new Date();
      let add = days + (d.getHours() >= cutoff ? 1 : 0);
      while (add > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() % 6 !== 0) add--;
      }
      const out = $('[data-hs-eta-date]', this);
      if (out) out.textContent = d.toLocaleDateString(document.documentElement.lang || 'en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
      this.hidden = false;
    }
  });

  /* ---------- <hs-product> buy box ---------- */
  define('hs-product', class extends HTMLElement {
    connectedCallback() {
      this.d = productData();
      if (!this.d) return;
      this.d.tiers.sort((a, b) => a.qty - b.qty);
      this.form = $('form[data-hs-form]', this);
      this.idInput = $('input[name="id"]', this.form);
      this.qtyInput = $('[data-hs-qty]', this);
      this.atc = $('[data-hs-atc]', this);
      this.gallery = document.querySelector('hs-gallery');

      this.addEventListener('change', e => {
        if (e.target.matches('[data-hs-option]')) this.onOption();
        else if (e.target.matches('[data-hs-gfci-variant]')) this.onGfciVariant();
        else if (e.target.matches('[name="hs-gfci"]')) this.render();
        else if (e.target === this.qtyInput) this.setQty(this.qtyInput.value);
      });
      this.addEventListener('click', e => {
        const s = e.target.closest('[data-hs-qty-step]');
        if (s) this.setQty(+this.qtyInput.value + +s.dataset.hsQtyStep);
        const smp = e.target.closest('[data-hs-sample-add]');
        if (smp) { e.preventDefault(); this.addSample(smp); }
      });
      this.form.addEventListener('submit', e => { e.preventDefault(); this.submit(); });
      document.addEventListener('hs:select', e => this.selectValue(e.detail));
      document.addEventListener('hs:submit', () => this.submit());

      this.variant = this.d.variants.find(v => v.id == this.idInput.value) || this.d.variants[0];
      this.syncQtyLimit();
      this.onGfciVariant();
    }

    /* The chosen GFCI variant (or the product's only one). Each variant can carry its own amp
       capacity, so the unit count follows whichever unit the shopper picked. */
    gfciVariant() {
      const g = this.d.gfci;
      if (!g) return null;
      const list = g.variants || [];
      const sel = $('[data-hs-gfci-variant]', this);
      if (sel) {
        const picked = list.find(x => x.id == sel.value);
        if (picked) return picked;
      }
      return list.find(x => x.id == g.id) || list[0] || g;
    }

    onGfciVariant() {
      const gv = this.gfciVariant();
      const add = $('[name="hs-gfci"][value="add"]', this);
      if (add && gv) {
        add.disabled = !gv.available;
        if (add.disabled && add.checked) add.checked = false;
      }
      this.render();
    }

    currentOptions() {
      return this.d.optionNames.map((_, i) => $(`[data-hs-option="${i}"]:checked`, this)?.value ?? null);
    }

    selectValue(value) {
      // Buttons are radios; dropdowns put data-hs-option on each <option> (and on the <select>).
      // Use-case buttons send a short name ("Walkway"); option values carry dimensions
      // ("Walkway Mat: 48″ x 36″"). Exact match first, then the shortest value whose name
      // starts with it, so "Walkway" picks Walkway Mat rather than Walkway Mat XL.
      const all = $$('[data-hs-option]', this).filter(i => i.tagName !== 'SELECT');
      const want = String(value || '').trim().toLowerCase();
      const name = v => String(v).split(':')[0].trim().toLowerCase();
      let input = all.find(i => i.value.trim().toLowerCase() === want)
        || all.filter(i => name(i.value) === want)[0]
        || all.filter(i => name(i.value).startsWith(want)).sort((a, b) => name(a.value).length - name(b.value).length)[0]
        || all.filter(i => i.value.toLowerCase().includes(want)).sort((a, b) => a.value.length - b.value.length)[0];
      if (!input) return;
      if (input.tagName === 'OPTION') input.selected = true; else input.checked = true;
      this.onOption();
    }

    onOption() {
      const v = findVariant(this.d, this.currentOptions());
      this.variant = v || null;
      if (v) {
        this.idInput.value = v.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true })); // Shop Pay installments banner listens
        // On phones the installments line sits outside the form, so update it directly too
        $$('shopify-payment-terms', this.closest('.hs-main') || document).forEach(t => t.setAttribute('variant-id', v.id));
        if (this.d.updateUrl) history.replaceState(null, '', `${this.d.url}?variant=${v.id}`);
        if (v.media && this.gallery) this.gallery.goToMedia(v.media);
        this.syncQtyLimit();
        // The theme's own variant event, so store features listening for it (the
        // estimated-shipping snippet, apps) follow this buy box too.
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: {
          id: v.id, title: v.title, available: v.available, price: v.price, compare_at_price: v.compare || null, options: v.options
        } } }));
      }
      this.render();
    }

    // Highest quantity that can be bought: the stock on hand when Shopify tracks it and
    // doesn't allow overselling, otherwise 99.
    maxQty() {
      const v = this.variant;
      if (v && v.tracked === 'shopify' && v.policy === 'deny' && v.inv > 0) return Math.min(99, v.inv);
      return 99;
    }

    // Apply the stock cap for the current variant: clamp the quantity, set max, update the +/- buttons.
    syncQtyLimit(n = this.qtyInput.value) {
      const max = this.maxQty();
      n = Math.max(1, Math.min(max, parseInt(n, 10) || 1));
      this.qtyInput.value = n;
      this.qtyInput.max = max;
      $$('[data-hs-qty-step="1"]', this).forEach(b => { b.disabled = n >= max; });
      $$('[data-hs-qty-step="-1"]', this).forEach(b => { b.disabled = n <= 1; });
    }

    setQty(n) {
      this.syncQtyLimit(n);
      this.render();
    }

    // Used by the sticky bar: set the power unit choice ('add' | 'have'), as if picked in the buy box.
    setGfci(value) {
      const input = $(`[name="hs-gfci"][value="${value}"]`, this);
      if (!input || input.disabled) return;
      input.checked = true;
      this.render();
    }

    // Used by the sticky bar: choose a value for option i, as if picked in the buy box.
    setOption(i, value) {
      const input = $$(`[data-hs-option="${i}"]`, this).find(el => el.tagName !== 'SELECT' && el.value === value);
      if (!input) return;
      if (input.tagName === 'OPTION') input.selected = true; else input.checked = true;
      this.onOption();
    }

    renderPickup(box, list) {
      box.hidden = !list.length;
      if (!list.length) return;
      const esc = t => String(t ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const first = list[0];
      const lines = list.map(a => {
        const [street, city] = String(a.address || '').split('|');
        return `<li><strong>${esc(a.name)}</strong>`
          + `<span class="hs-pickup__status${a.available ? '' : ' is-out'}">${a.available ? 'Pickup available' + (a.time ? ', ' + esc(a.time).toLowerCase() : '') : 'Pickup currently unavailable'}</span>`
          + (street ? `<span>${esc(street)}</span>` : '') + (city ? `<span>${esc(city)}</span>` : '')
          + (a.phone ? `<a href="tel:${esc(a.phone).replace(/\s/g, '')}">${esc(a.phone)}</a>` : '') + '</li>';
      }).join('');
      const body = $('[data-hs-pickup-body]', box);
      const wasOpen = !!$('details[open]', body);
      body.innerHTML =
        `<p class="hs-pickup__line">${first.available ? `<strong>Pickup available</strong> at ${esc(first.name)}` : `Pickup currently unavailable at ${esc(first.name)}`}</p>`
        + (first.available && first.time ? `<p class="hs-pickup__time">${esc(first.time)}</p>` : '')
        + `<details class="hs-pickup__more"${wasOpen ? ' open' : ''}><summary>${list.length > 1 ? 'Check other stores' : 'Store information'}</summary><ul class="hs-pickup__list" role="list">${lines}</ul></details>`;
    }

    calc() {
      const v = this.variant;
      const qty = +this.qtyInput.value || 1;
      const amps = qty * (v?.amps || this.d.defaultAmps);
      const gv = this.gfciVariant();
      const perUnit = gv?.amps || this.d.gfci?.amps || 13;
      const units = Math.max(1, Math.ceil(amps / perUnit));
      const tier = this.d.tiers.filter(t => qty >= t.qty).pop() || null;
      const next = this.d.tiers.find(t => qty < t.qty) || null;
      const gfci = $('[name="hs-gfci"]:checked', this)?.value || null;
      return { v, qty, amps, units, perUnit, gv, tier, next, gfci };
    }

    render() {
      const { v, qty, amps, units, perUnit, gv, tier, next, gfci } = this.calc();
      const f = this.d.moneyFormat;
      const set = (sel, txt) => $$(sel, this).forEach(n => { n.textContent = txt; });
      const sizeIdx = this.d.sizeIndex;
      const opts = this.currentOptions();
      this.syncSample();

      // Colour swatches: name of the chosen colour beside the label; colours with no available
      // variant (for the other choices) are crossed out
      $$('[data-hs-swatch]', this).forEach(inp => {
        const o = opts.slice(); o[+inp.dataset.hsOption] = inp.value;
        const m = findVariant(this.d, o);
        inp.nextElementSibling?.classList.toggle('is-out', !m || !m.available);
      });
      $$('[data-hs-swatch-name]', this).forEach(n => {
        const c = $('[data-hs-swatch]:checked', n.closest('.hs-opt'));
        if (c) n.textContent = c.value;
      });

      // Per-size row: price + availability for the currently chosen model
      if (sizeIdx > -1) {
        $$(`[data-hs-option="${sizeIdx}"]`, this).forEach(input => {
          if (input.tagName === 'SELECT') return;
          const o = opts.slice(); o[sizeIdx] = input.value;
          const match = findVariant(this.d, o);
          if (input.tagName === 'OPTION') {
            // Dropdown: name — dimensions — price for the chosen model, flagged when sold out
            const out = !match || !match.available;
            input.textContent = [input.dataset.label || input.value, input.dataset.dims, match ? money(match.price, f) : null]
              .filter(Boolean).join(' — ') + (out ? ' (sold out)' : '');
            return;
          }
          const row = input.closest('[data-hs-choice]');
          row.classList.toggle('is-out', !match || !match.available);
          const p = $('[data-hs-row-price]', row);
          if (p) p.textContent = match ? money(match.price, f) : '—';
        });
      }

      const soldOut = !v || !v.available;
      const needsGfci = this.d.gfciRequired && !!this.d.gfci;
      const ready = !soldOut && (!needsGfci || !!gfci);

      // Price block. On phones the price row sits above the gallery (outside the buy box),
      // so look for it across the whole product area.
      if (v) {
        const area = this.closest('.hs-main') || this;
        const pset = (sel, txt) => $$(sel, area).forEach(n => { n.textContent = txt; });
        pset('[data-hs-price]', money(v.price, f));
        $$('[data-hs-sku]', area).forEach(n => {
          const wrap = n.closest('[data-hs-sku-wrap]');
          n.textContent = v.sku || ''; wrap.hidden = !v.sku;
          const c = $('[data-hs-copy]', wrap); if (c) c.dataset.hsCopy = v.sku || '';
        });
        const onSale = v.compare > v.price;
        $$('[data-hs-sale]', area).forEach(n => { n.hidden = !onSale; });
        $('[data-hs-price]', area)?.classList.toggle('is-sale', onSale);
        if (onSale) {
          pset('[data-hs-compare]', money(v.compare, f));
          pset('[data-hs-save]', money(v.compare - v.price, f));
        }
        set('[data-hs-size-summary]', [v.label || shortLabel(v.options[sizeIdx] ?? v.title), v.dims].filter(Boolean).join(' · '));
      }

      // Readout
      const model = this.d.modelIndex > -1 && v ? v.options[this.d.modelIndex] : this.d.productTitle;
      const sub = v ? v.price * qty : 0;
      const disc = tier ? Math.round(sub * tier.pct / 100) : 0;
      const gfciCost = gfci === 'add' && gv ? gv.price * units : 0;
      set('[data-hs-ro-line]', `${qty} × ${v ? (v.label || shortLabel(v.options[sizeIdx] ?? v.title)) : '—'} · ${model}`);
      set('[data-hs-ro-sub]', money(sub, f));
      set('[data-hs-ro-units]', `${units} ×`);
      set('[data-hs-ro-amps]', String(perUnit));
      set('[data-hs-ro-tier]', tier ? `Buy ${tier.qty} · −${tier.pct}%` : 'None yet');
      set('[data-hs-ro-total]', money(sub - disc + gfciCost, f) + (gfci === 'add' || !needsGfci ? '' : ' + GFCI'));
      set('[data-hs-tier-hint]', next
        ? `Add ${next.qty - qty} more to reach Buy ${next.qty} · −${next.pct}%.`
        : (tier ? `You're at the best tier — Buy ${tier.qty} · −${tier.pct}%.` : ''));

      // GFCI
      if (this.d.gfci && gv) {
        const unitName = cleanVariantTitle(gv.title) || this.d.gfci.title || 'GFCI Power Unit';
        set('[data-hs-gfci-add]', gv.available
          ? `Add ${units} × ${unitName} (+${money(gv.price * units, f)})`
          : `${unitName} — sold out`);
        set('[data-hs-gfci-helper]', gfci
          ? `Your ${qty > 1 ? qty + ' mats draw' : 'mat draws'} ${amps.toFixed(2)}A — that needs ${units} × ${unitName}. One of those carries ${perUnit}A.`
          : `Add to Cart stays disabled until you choose. ${unitName} powers up to ${perUnit}A.`);
      }

      // Stock status (+ inventory level when Shopify tracks it) beside the delivery estimate
      const stock = $('[data-hs-stock]', this);
      const avail = $('[data-hs-avail]', this);
      if (stock) {
        const ok = !!(v && v.available);
        const showInv = avail?.dataset.inventory !== 'false';
        stock.classList.toggle('is-out', !ok);
        set('[data-hs-stock-label]', !v ? 'Unavailable' : !ok ? 'Out of stock'
          : (showInv && v.tracked === 'shopify' && v.inv > 0) ? `In stock · ${v.inv} available` : 'In stock');
      }

      // Estimated ship date (store's estimated-shipping snippet) replaces "Get it by"
      // when the variant has an estimate above 0 or a future in-stock date.
      const ship = $('[data-hs-ship]', this);
      if (ship) {
        const min = parseFloat(String(v?.ship || '').split(/[-–—]/)[0]);
        let future = false;
        if (v?.instock) { const d = new Date(v.instock), t = new Date(); d.setHours(0,0,0,0); t.setHours(0,0,0,0); future = d >= t; }
        const useShip = !!(v && v.available && ((min > 0) || future));
        ship.classList.toggle('is-off', !useShip);
        $('hs-eta', this)?.classList.toggle('is-off', useShip);
        stock?.classList.toggle('is-off', useShip);   // with a ship estimate, that line alone says when it ships
      }

      // Pickup (Shopify local pickup, per variant)
      const pick = $('[data-hs-pickup]', this);
      if (pick && v) this.renderPickup(pick, v.pickup || []);

      // Fit warning block: "multi" only matters once there is more than one mat to lay out
      $$('[data-hs-fit="multi"]', this).forEach(n => { n.hidden = qty < 2; });

      // Notify-me panel
      const notify = $('[data-hs-notify]', this);
      if (notify) {
        notify.hidden = !(v && !v.available);
        set('[data-hs-notify-title]', v ? `${v.title} is sold out` : '');
        const body = $('[name="contact[body]"]', notify);
        if (body && v) body.value = `Back-in-stock request: ${this.d.productTitle} — ${v.title} (variant ${v.id})`;
      }

      // ATC
      // Everything this button adds: the mat, the power unit (when added) and ticked "Complete the order" items
      const fbtEl = document.querySelector('hs-fbt');
      const gfciOn = gfci === 'add' && !!gv?.available;
      const grand = v ? v.price * qty + (gfciOn ? gv.price * units : 0) + (fbtEl?.extraCents?.() || 0) : 0;
      if (fbtEl?.update) fbtEl.update({ v, qty, gv, units, gfciOn, total: grand, f });
      const label = !v ? 'Unavailable' : soldOut ? 'Sold out' : !ready ? 'Choose a power option to continue' : `Add to Cart · ${money(grand, f)}`;
      this.atc.disabled = !ready;
      set('[data-hs-atc-label]', label);

      document.dispatchEvent(new CustomEvent('hs:state', {
        detail: {
          ready, soldOut, label,
          // Short variant label for the sticky bar, e.g. "HotStep · Stair Mat"
          summary: v ? v.options.map((o, i) => (i === sizeIdx && v.label) ? v.label : o).join(' · ') : '',
          price: v ? money(v.price, f) : '',
          onSale: v ? v.compare > v.price : false,
          options: v ? v.options : this.currentOptions(),
          // Shown in the sticky bar so it's clear the power unit goes in with the mat
          addon: gfci === 'add' && gv && gv.available
            ? `+ ${units} × ${cleanVariantTitle(gv.title) || this.d.gfci?.title || 'GFCI Power Unit'} (${money(gv.price * units, f)})`
            : '',
          needsChoice: needsGfci && !gfci,
          hasGfci: !!(this.d.gfci && gv),
          gfci: gfci || '',
          gfciAddLabel: gv ? (gv.available ? `Add power unit (+${money(gv.price * units, f)})` : 'Power unit sold out') : '',
          gfciAddDisabled: !!(gv && !gv.available),
          gfciShort: gfci === 'add' && gv ? `Added (+${money(gv.price * units, f)})` : gfci === 'have' ? 'I already have one' : 'Choose one',
          qty, max: this.maxQty(),
          image: v ? v.img : null
        }
      }));
    }

    // "Order a sample": the sample variant that matches the other chosen options (e.g. colour)
    sampleVariant() {
      const box = $('[data-hs-sample]', this);
      if (!box) return null;
      const idx = +box.dataset.index, val = box.dataset.value;
      const opts = this.currentOptions(); opts[idx] = val;
      const exact = findVariant(this.d, opts);
      if (exact && exact.available) return exact;
      return this.d.variants.find(x => x.options[idx] === val && x.available) || null;
    }
    syncSample() {
      const box = $('[data-hs-sample]', this);
      if (!box) return;
      const sv = this.sampleVariant();
      box.hidden = !sv;
      const lab = $('[data-hs-sample-price]', box);
      if (sv && lab) lab.textContent = sv.price === 0 ? 'Free' : money(sv.price, this.d.moneyFormat);
    }
    async addSample(btn) {
      const sv = this.sampleVariant();
      const msg = $('[data-hs-sample-msg]', this);
      if (!sv) return;
      if (msg) msg.hidden = true;
      const r = await cartAdd([{ id: sv.id, quantity: 1, properties: { 'Estimated to Ship by': cartShipEstimate(sv) } }], btn);
      if (r !== true && msg) { msg.textContent = r; msg.hidden = false; }
    }

    async submit() {
      const { v, qty, units, gv, gfci } = this.calc();
      if (this.atc.disabled || !v) { jump(this); return; }
      // Line item properties, as the theme's product form sends them: every enabled
      // properties[...] field of this form (including fields apps inject), plus the ship estimate.
      const properties = {};
      Array.from(this.form.elements).forEach(el => {
        if (!el.name || el.disabled || !el.name.startsWith('properties[') || el.type === 'file') return;
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        properties[el.name.slice(11, -1)] = el.value;
      });
      const est = cartShipEstimate(v);
      properties['Estimated to Ship by'] = est;
      const estInput = $('#mat-shipping-info', this);
      if (estInput) { estInput.value = est; }
      const items = [{ id: v.id, quantity: qty, properties }];
      if (gfci === 'add' && gv?.available) items.push({ id: gv.id, quantity: units, properties: { 'Estimated to Ship by': cartShipEstimate(gv) } });
      // Frequently bought together: ticked items go in with the mat
      const fbt = document.querySelector('hs-fbt');
      if (fbt && typeof fbt.items === 'function') items.push(...fbt.items());
      const err = $('[data-hs-error]', this);
      err.hidden = true;
      const r = await cartAdd(items, this.atc);
      if (r !== true) { err.textContent = r; err.hidden = false; }
    }
  });

  /* ---------- <hs-fbt>: "Complete the order" ----------
     The mat (and the power unit, when added) plus the product's complementary products (Search &
     Discovery) or a manual list. Ticked items go into the cart with the mat; the total and the Add to
     Cart price include them. A Custom size / logo product only links to its page (Customize ›).
     The first logo product also fills the "Own a business?" banner when it has no product set. */
  define('hs-fbt', class extends HTMLElement {
    async connectedCallback() {
      // Power unit line: ticking it is the same as choosing Add / I already have one in the buy box
      this.addEventListener('change', e => {
        if (e.target.matches('[data-fbt-gfci-check]')) document.querySelector('hs-product')?.setGfci?.(e.target.checked ? 'add' : 'have');
      });
      const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
      const limit = +this.dataset.limit || 3;
      let products = [];
      try {
        const manual = (this.dataset.manual || '').split(',').filter(Boolean);
        if (manual.length) {
          products = (await Promise.all(manual.map(h => fetch(`/products/${h}.js`).then(r => r.ok ? r.json() : null).catch(() => null)))).filter(Boolean);
        } else {
          products = (await (await fetch(this.dataset.url)).json()).products || [];
        }
      } catch (_) { return; }
      products = products.filter(p => p && p.available && !exclude.includes(p.handle));
      // Upgrade banner without a product of its own: take the first logo product
      const banner = document.querySelector('[data-hs-upsell][data-auto]');
      if (banner) {
        const up = products.find(p => this.isLogo(p));
        if (up) { this.fillBanner(banner, up); products = products.filter(p => p !== up); }
      }
      this.products = products.slice(0, limit);
      if (!this.products.length) return;
      const list = $('[data-hs-fbt-list]', this);
      list.insertAdjacentHTML('beforeend', this.products.map((p, i) => this.row(p, i)).join(''));
      this.products.forEach((p, i) => this.sync(i));
      this.addEventListener('change', e => {
        const li = e.target.closest('[data-i]'); if (!li) return;
        if (e.target.matches('[data-fbt-qty]')) e.target.value = Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1));
        this.sync(+li.dataset.i); this.changed();
      });
      this.addEventListener('click', e => {
        const st = e.target.closest('[data-fbt-step]'); if (!st) return;
        const li = st.closest('[data-i]'); const q = $('[data-fbt-qty]', li);
        q.value = Math.max(1, Math.min(99, (+q.value || 1) + +st.dataset.fbtStep)); this.sync(+li.dataset.i); this.changed();
      });
      this.hidden = false;
      this.changed();
    }
    fillBanner(a, p) {
      a.href = p.url;
      const img = this.img(p);
      if (img) $('.hs-upsell__img', a).innerHTML = `<img src="${img}" alt="" width="46" height="46" loading="lazy">`;
      const n = $('[data-upsell-name]', a); n.textContent = `${n.textContent.trim()} ${p.title}`;
      a.hidden = false;
    }
    changed() { document.querySelector('hs-product')?.render?.(); }
    esc(t) { return String(t ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
    img(p) { const u = p.featured_image; if (!u) return ''; const x = u.startsWith('//') ? 'https:' + u : u; return x + (x.includes('?') ? '&' : '?') + 'width=120'; }
    isLogo(p) { return /logo/i.test(p.title); }
    isCustomVariant(v) { return !!v && (v.options || [v.title]).some(o => /custom/i.test(o)); }
    standard(p) { return p.variants.filter(v => !this.isCustomVariant(v)); }
    firstVariant(p) { const std = this.standard(p); return std.find(v => v.available) || std[0] || null; }
    customUrl(p) {
      const c = p.variants.find(v => this.isCustomVariant(v) && v.available) || p.variants.find(v => this.isCustomVariant(v));
      return c ? `${p.url}${p.url.includes('?') ? '&' : '?'}variant=${c.id}` : p.url;
    }
    hasCustom(p) { return this.isLogo(p) || p.variants.some(v => this.isCustomVariant(v)); }
    row(p, i) {
      const v = this.firstVariant(p);
      const std = this.isLogo(p) ? [] : this.standard(p);
      const opts = std.length > 1 ? `<select class="hs-fbt__select" data-fbt-variant aria-label="${this.esc(p.title)} option">${std.map(x => `<option value="${x.id}"${x.id === v.id ? ' selected' : ''}${x.available ? '' : ' disabled'}>${this.esc(x.title)}${x.available ? '' : ' (sold out)'}</option>`).join('')}</select>` : (v && std.length === 1 && !/default title/i.test(v.title) ? `<span>${this.esc(v.title)}</span>` : '');
      const img = this.img(p);
      return `<li class="hs-fbt__line" data-i="${i}">
        <label class="hs-fbt__check" data-fbt-check-wrap><input type="checkbox" data-fbt-check aria-label="Add ${this.esc(p.title)}"><span aria-hidden="true"></span></label>
        <a class="hs-fbt__img" href="${p.url}" tabindex="-1" aria-hidden="true">${img ? `<img src="${img}" alt="" width="52" height="52" loading="lazy">` : ''}</a>
        <span class="hs-fbt__body">
          <a class="hs-fbt__name" href="${p.url}">${this.esc(p.title)}</a>
          <span class="hs-fbt__sub">${opts}<span class="hs-qty hs-fbt__qty" data-fbt-qty-wrap><button type="button" data-fbt-step="-1" aria-label="Decrease quantity">−</button><input type="number" min="1" max="99" value="1" inputmode="numeric" data-fbt-qty aria-label="Quantity"><button type="button" data-fbt-step="1" aria-label="Increase quantity">+</button></span>${this.hasCustom(p) ? `<a class="hs-fbt__custom" href="${this.customUrl(p)}">${v ? 'Custom size ›' : 'Customize ›'}</a>` : ''}</span>
        </span>
        <span class="hs-fbt__price" data-fbt-price></span>
      </li>`;
    }
    variantOf(i) {
      const p = this.products[i], li = $(`[data-i="${i}"]`, this), sel = $('[data-fbt-variant]', li);
      return sel ? p.variants.find(x => x.id == sel.value) : this.firstVariant(p);
    }
    sync(i) {
      const p = this.products[i], li = $(`[data-i="${i}"]`, this), v = this.isLogo(p) ? null : this.variantOf(i);
      const q = +($('[data-fbt-qty]', li).value) || 1;
      const custom = !v;
      $('[data-fbt-qty-wrap]', li).hidden = custom;
      const chk = $('[data-fbt-check]', li);
      $('[data-fbt-check-wrap]', li).classList.toggle('is-off', custom);
      chk.disabled = custom; if (custom) chk.checked = false;
      li.classList.toggle('is-custom', custom);
      const lowest = Math.min(...p.variants.map(x => x.price).filter(x => x > 0));
      $('[data-fbt-price]', li).textContent = custom ? (isFinite(lowest) ? 'From ' + money(lowest, this.dataset.money) : '') : money(v.price * q, this.dataset.money);
    }
    // Cents of the ticked items (for the total and the Add to Cart price)
    extraCents() {
      return this.items().reduce((sum, it) => {
        const p = this.products.find(x => x.variants.some(v => v.id === it.id));
        const v = p && p.variants.find(x => x.id === it.id);
        return sum + (v ? v.price * it.quantity : 0);
      }, 0);
    }
    // Called by the buy box on every render: the mat and power unit lines, and the total
    update({ v, qty, gv, units, gfciOn, total, f }) {
      if (!v) return;
      const sub = $('[data-fbt-main-sub]', this);
      if (sub) sub.textContent = [...(v.options || []).filter(o => !/default title/i.test(o)), `qty ${qty}`].join(' · ');
      const mp = $('[data-fbt-main-price]', this); if (mp) mp.textContent = money(v.price * qty, f);
      if (v.img) { const im = $('[data-fbt-main-img]', this); if (im && im.getAttribute('src') !== v.img) im.src = v.img; }
      const g = $('[data-fbt-gfci]', this);
      if (g) {
        // Shown whenever there's a power unit to offer; the tick mirrors the buy box's power choice
        g.hidden = !gv;
        if (gv) {
          const c = $('[data-fbt-gfci-check]', g);
          c.checked = gfciOn; c.disabled = !gv.available;
          g.classList.toggle('is-unticked', !gfciOn);
          $('[data-fbt-gfci-sub]', g).textContent = `qty ${units}`;
          $('[data-fbt-gfci-price]', g).textContent = money(gv.price * units, f);
        }
      }
      const t = $('[data-fbt-total]', this); if (t) t.textContent = money(total, f);
    }
    // Called by the buy box when it adds to cart
    items() {
      return (this.products || []).map((p, i) => {
        const li = $(`[data-i="${i}"]`, this), chk = $('[data-fbt-check]', li), v = this.variantOf(i);
        if (!chk || !chk.checked || chk.disabled || !v || !v.available) return null;
        return { id: v.id, quantity: +($('[data-fbt-qty]', li).value) || 1 };
      }).filter(Boolean);
    }
  });

  /* <hs-atc-proxy> and <hs-sticky-atc> live in cm-sticky.js (shared with the other product pages). */

  /* ---------- <hs-ugc>: customer video circles + lightbox ----------
     Circles play a muted preview on hover (devices that hover); a click opens a lightbox with
     the full video (with sound) or photo, previous / next, arrow keys, swipe, Esc to close. */
  define('hs-ugc', class extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-hs-ugc-item]', this);
      if (!this.items.length) return;
      const hover = matchMedia('(hover: hover)').matches;
      this.items.forEach((btn, i) => {
        btn.addEventListener('click', () => this.open(i));
        const v = $('video', btn);
        if (!v || !hover) return;
        btn.addEventListener('mouseenter', () => {
          if (!v.src) v.src = v.dataset.src;
          v.play().catch(() => {});
        });
        btn.addEventListener('mouseleave', () => v.pause());
      });
    }
    build() {
      if (this.dlg) return;
      const icon = d => `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
      const dlg = document.createElement('dialog');
      dlg.className = 'hs-vlb';
      dlg.setAttribute('aria-label', 'Customer videos');
      dlg.innerHTML = `
        <button type="button" class="hs-vlb__close" data-close aria-label="Close">${icon('M18 6 6 18M6 6l12 12')}</button>
        <button type="button" class="hs-vlb__nav hs-vlb__nav--prev" data-step="-1" aria-label="Previous">${icon('m15 18-6-6 6-6')}</button>
        <div class="hs-vlb__stage" data-stage></div>
        <button type="button" class="hs-vlb__nav hs-vlb__nav--next" data-step="1" aria-label="Next">${icon('m9 18 6-6-6-6')}</button>
        <p class="hs-vlb__count" data-count aria-live="polite"></p>`;
      document.body.appendChild(dlg);
      dlg.addEventListener('click', e => {
        if (e.target === dlg || e.target.closest('[data-close]')) return this.close();
        const step = e.target.closest('[data-step]');
        if (step) this.show(this.index + +step.dataset.step);
      });
      dlg.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight') this.show(this.index + 1);
        if (e.key === 'ArrowLeft') this.show(this.index - 1);
      });
      dlg.addEventListener('close', () => { this.stop(); $('[data-stage]', dlg).innerHTML = ''; document.documentElement.classList.remove('hs-vlb-open'); });
      let x0 = null;
      dlg.addEventListener('touchstart', e => { x0 = e.touches[0].clientX; }, { passive: true });
      dlg.addEventListener('touchend', e => {
        if (x0 == null) return;
        const dx = e.changedTouches[0].clientX - x0; x0 = null;
        if (Math.abs(dx) > 50) this.show(this.index + (dx < 0 ? 1 : -1));
      });
      this.dlg = dlg;
    }
    open(i) {
      this.build();
      this.items.forEach(b => $('video', b)?.pause());
      if (!this.dlg.open) this.dlg.showModal();
      document.documentElement.classList.add('hs-vlb-open');
      this.show(i);
    }
    show(i) {
      const n = this.items.length;
      this.index = (i + n) % n;
      const btn = this.items[this.index];
      const stage = $('[data-stage]', this.dlg);
      this.stop();
      stage.innerHTML = '';                      // also stops an embedded player
      const label = btn.getAttribute('aria-label') || '';
      if (btn.dataset.type === 'video') {
        stage.innerHTML = `<video class="hs-vlb__media" controls playsinline autoplay ${btn.dataset.poster ? `poster="${btn.dataset.poster}"` : ''}></video>`;
        const v = $('video', stage);
        v.setAttribute('aria-label', label.replace(/^Play /, ''));
        v.src = btn.dataset.src;
        v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });   // browsers may block sound until a tap
      } else if (btn.dataset.type === 'embed') {
        // YouTube / Shorts / Vimeo player; Shorts are vertical, other links 16:9
        stage.innerHTML = `<iframe class="hs-vlb__media hs-vlb__frame hs-vlb__frame--${btn.dataset.shape || 'wide'}" src="${btn.dataset.src}" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>`;
        $('iframe', stage).title = label.replace(/^Play /, '');
      } else {
        stage.innerHTML = `<img class="hs-vlb__media" src="${btn.dataset.src}" alt="">`;
        $('img', stage).alt = label.replace(/^View /, '');
      }
      $$('[data-step]', this.dlg).forEach(b => { b.hidden = n < 2; });
      $('[data-count]', this.dlg).textContent = n > 1 ? `${this.index + 1} / ${n}` : '';
    }
    stop() { $('video', this.dlg || this)?.pause(); }
    close() {
      this.stop();
      this.dlg.close();
      document.documentElement.classList.remove('hs-vlb-open');
      this.items[this.index]?.focus();
    }
  });

  /* ---------- Phones/tablets: price row + installments move above the gallery ----------
     The same elements are moved (not copied), so prices, sale state, the copy-code pill and
     Shop Pay installments keep working; they move back on wider screens. */
  (() => {
    const head = document.querySelector('[data-hs-mhead-price]');
    if (!head) return;
    const nodes = ['.hs-main__info .hs-buy__price-row', '.hs-main__info .hs-buy__installments']
      .map(sel => document.querySelector(sel)).filter(Boolean)
      .map(n => ({ n, home: n.parentNode, next: n.nextSibling }));
    if (!nodes.length) return;
    const mq = matchMedia('(max-width: 989px)');
    const place = () => nodes.forEach(({ n, home, next }) => {
      if (mq.matches) { if (n.parentNode !== head) head.appendChild(n); }
      else if (n.parentNode !== home) home.insertBefore(n, next && next.parentNode === home ? next : null);
    });
    place();
    mq.addEventListener?.('change', place);
  })();

  /* ---------- One-line title (phones): shrink to fit, down to 14px; wrap only if still too long ---------- */
  (() => {
    const els = $$('[data-hs-fit]');
    if (!els.length) return;
    const fit = () => els.forEach(el => {
      el.style.fontSize = ''; el.classList.remove('is-wrap');
      if (!el.offsetParent) return;
      let size = parseFloat(getComputedStyle(el).fontSize);
      while (el.scrollWidth > el.clientWidth + 1 && size > 14) { size -= 0.5; el.style.fontSize = size + 'px'; }
      if (el.scrollWidth > el.clientWidth + 1) el.classList.add('is-wrap');
    });
    fit();
    let t; addEventListener('resize', () => { clearTimeout(t); t = setTimeout(fit, 120); });
    document.fonts?.ready.then(fit);
  })();

  /* ---------- <hs-recommendations>: fetch when near viewport ---------- */
  define('hs-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (this.children.length) return;
      const io = new IntersectionObserver(async ([en]) => {
        if (!en.isIntersecting) return;
        io.disconnect();
        try {
          const html = await (await fetch(this.dataset.url)).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('hs-recommendations');
          if (!fresh || !fresh.innerHTML.trim()) throw new Error('empty');
          this.innerHTML = fresh.innerHTML;
        } catch (_) {
          this.closest('.shopify-section')?.setAttribute('hidden', '');
        }
      }, { rootMargin: '0px 0px 600px 0px' });
      io.observe(this);
    }
  });

  /* ---------- <hs-section-nav> active-link tracking ---------- */
  define('hs-section-nav', class extends HTMLElement {
    connectedCallback() {
      const links = $$('a[href^="#"]', this);
      const map = new Map();
      links.forEach(a => { const t = $(a.getAttribute('href')); if (t) map.set(t, a); else a.hidden = true; });
      // Sections with no content don't render, so their links are hidden above; none left = no bar.
      if (!map.size) { (this.closest('.shopify-section') || this).hidden = true; return; }
      const io = new IntersectionObserver(entries => {
        entries.forEach(en => {
          if (!en.isIntersecting) return;
          links.forEach(a => a.removeAttribute('aria-current'));
          const a = map.get(en.target);
          a.setAttribute('aria-current', 'true');
          a.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      }, { rootMargin: '-30% 0px -60% 0px' });
      map.forEach((_, t) => io.observe(t));
    }
  });

  /* ---------- <hs-compare> add-to-cart per model ---------- */
  define('hs-compare', class extends HTMLElement {
    connectedCallback() {
      this.d = productData();
      if (!this.d) return;
      this.addEventListener('change', e => { if (e.target.matches('select[data-model]')) this.update(e.target); });
      this.addEventListener('click', async e => {
        const b = e.target.closest('button[data-model]');
        if (!b || !b.dataset.variant) return;
        const r = await cartAdd([{ id: +b.dataset.variant, quantity: 1 }], b);
        if (r !== true) alertNear(b, r);
      });
      // Option values carry their dimensions ("Walkway: 48″ × 36″"); show them the way the
      // buy box does — short name · dimensions — without touching the value used for matching.
      $$('select[data-model]', this).forEach(s => {
        Array.from(s.options).forEach(o => {
          const v = this.match(s.dataset.model, o.value);
          o.textContent = this.sizeLabel(v, o.value);
        });
        this.update(s);
      });
    }
    match(model, size) {
      const d = this.d;
      const o = d.optionNames.map(() => null);
      if (d.modelIndex > -1) o[d.modelIndex] = model;
      if (d.sizeIndex > -1) o[d.sizeIndex] = size;
      return findVariant(d, o);
    }
    sizeLabel(v, fallback) {
      if (!v) return shortLabel(fallback);
      return [v.label || shortLabel(fallback), v.dims].filter(Boolean).join(' · ');
    }
    update(select) {
      const d = this.d;
      const model = select.dataset.model;
      const v = this.match(model, select.value);
      const btn = $(`button[data-model="${CSS.escape(model)}"]`, this);
      const cell = $(`[data-hs-cmp-price="${CSS.escape(model)}"]`, this);
      const ok = v && v.available;
      btn.disabled = !ok;
      btn.dataset.variant = ok ? v.id : '';
      btn.textContent = ok ? `Add to Cart · ${money(v.price, d.moneyFormat)}` : 'Sold out';
      if (cell) {
        const name = v ? (v.label || shortLabel(select.value)) : shortLabel(select.value);
        cell.textContent = v ? `${name} · ${money(v.price, d.moneyFormat)}` : '—';
      }
    }
  });
})();